import { gql, useApolloClient } from "@apollo/client";
import * as _ from "lodash";
import { useCallback, useMemo } from "react";
import { useOrganizationPath } from "src/hooks/useOrganizationPath";
import { FORM_TEMPLATE_FRAGMENT } from "src/operations/fragments/formTemplate";
import {
  GET_APPLICANT_DATE_OF_BIRTH_BY_FORM_ID,
  GET_FORM_ANSWERS_BY_ID,
} from "src/scenes/parent/forms/graphql/queries";
import { useEnv } from "src/services/env";
import * as AFService from "src/services/formTemplate";
import * as Answer from "src/services/formTemplate/answer";
import * as Question from "src/services/formTemplate/question";
import * as AF from "src/types/formTemplate";
import * as GQL from "src/types/graphql";
import { z } from "zod";
import { getAllEligibilityQuestions } from "../formTemplate/preRankingSection/determineEligibility";
import { useEligibilityEnabled } from "./EligibilityServiceProvider";

// Global cache outside of the component
const globalCache: { [key: string]: string[] } = {};

/* Types */
type Props = {
  formTemplateId: string;
};

type GetIneligibleSchoolIdsByAnswersFn = (value: {
  answers: Readonly<Answer.FormikValues>;
  dateOfBirth: string | null;
}) => Promise<string[]>;

type GetIneligibleSchoolIdsByFormIdFn = (value: {
  formId: string;
}) => Promise<string[]>;

type ShouldRunEligibilityCheckFn = (args: {
  questions: readonly AF.Question<AF.WithId>[];
  answers: { previous: Answer.FormikValues; current: Answer.FormikValues };
}) => boolean;

export function useEligibilityService({ formTemplateId }: Props) {
  const enabled = useEligibilityEnabled();
  const env = useEnv();
  const orgPath = useOrganizationPath();

  const getIneligibleSchoolIdsByAnswers: GetIneligibleSchoolIdsByAnswersFn =
    useCallback(
      async ({ answers, dateOfBirth }) => {
        if (!orgPath) {
          throw new EligibilityServiceError("Failed to get eligibility", {
            cause: "Missing orgPath from the route",
          });
        }

        const cacheKey = JSON.stringify({
          orgPath,
          formTemplateId,
          answers,
        });

        const cacheValue = globalCache[cacheKey];
        if (cacheValue) {
          return cacheValue;
        }

        try {
          const response = await fetch(
            `${env.VITE_ELIGIBILITY_SERVICE_URL}/organizations/${orgPath}/formTemplates/${formTemplateId}/findEligibility`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify(toPayload(answers, dateOfBirth)),
            }
          );
          if (response.status !== 200) {
            throw new Error("Failed to get eligibility", {
              cause: response.statusText,
            });
          }
          const raw = await response.json();
          const json = ResponseSchema.parse(raw);
          const result = json.ineligibleSchools.map((school) => school.id);

          // Store the result in the global cache
          globalCache[cacheKey] = result;
          return result;
        } catch (error) {
          console.error(error);
          throw new EligibilityServiceError("Failed to get eligibility", {
            cause: error,
          });
        }
      },
      [env.VITE_ELIGIBILITY_SERVICE_URL, formTemplateId, orgPath]
    );

  const apolloClient = useApolloClient();

  // TODO: https://app.asana.com/0/1207937326674859/1208484114249633/f
  const getIneligibleSchoolIdsByFormId: GetIneligibleSchoolIdsByFormIdFn =
    useCallback(
      async ({ formId }) => {
        const [questionsResponse, answersResponse, dateOfBirthResponse] =
          await Promise.all([
            apolloClient.query<
              GQL.GetFormTemplateByIdForEligibility,
              GQL.GetFormTemplateByIdForEligibilityVariables
            >({
              query: gql`
                ${FORM_TEMPLATE_FRAGMENT}

                query GetFormTemplateByIdForEligibility(
                  $form_template_id: uuid!
                ) {
                  form_template_by_pk(id: $form_template_id) {
                    ...FormTemplateFragment
                  }
                }
              `,
              variables: { form_template_id: formTemplateId },
            }),
            apolloClient.query<
              GQL.GetFormAnswersById,
              GQL.GetFormAnswersByIdVariables
            >({
              query: GET_FORM_ANSWERS_BY_ID,
              variables: { form_id: formId },
            }),
            apolloClient.query<
              GQL.GetApplicantDateOfBirthByFormId,
              GQL.GetApplicantDateOfBirthByFormIdVariables
            >({
              query: GET_APPLICANT_DATE_OF_BIRTH_BY_FORM_ID,
              variables: { formId },
            }),
          ]);
        const formTemplateData = questionsResponse.data.form_template_by_pk;
        if (!formTemplateData) {
          throw new Error("Unable to get eligibility", {
            cause: `Invalid formTemplateId: ${formTemplateId}`,
          });
        }
        const formTemplate = AFService.fromGQL(formTemplateData);
        const preRankingSection = AFService.findPreRankingSection(formTemplate);

        const answers = !preRankingSection
          ? {}
          : Answer.getFormikInitialValues(
              preRankingSection.questions,
              answersResponse.data.form_answer,
              answersResponse.data.grades_answer,
              answersResponse.data.form_address,
              answersResponse.data.custom_question_answer
            );

        const dateOfBirth =
          dateOfBirthResponse.data.form_by_pk?.person.birth_date;
        if (dateOfBirth === undefined || dateOfBirth === null) {
          throw new Error("Unable to get eligibility", {
            cause: `Missing applicant birth of date`,
          });
        }
        return getIneligibleSchoolIdsByAnswers({ answers, dateOfBirth });
      },
      [apolloClient, formTemplateId, getIneligibleSchoolIdsByAnswers]
    );

  const service = useMemo(
    () => ({
      getIneligibleSchoolIdsByAnswers,
      getIneligibleSchoolIdsByFormId,
      shouldRunEligibilityCheck,
      enabled,
    }),
    [getIneligibleSchoolIdsByAnswers, getIneligibleSchoolIdsByFormId, enabled]
  );

  if (!enabled) {
    return {
      getIneligibleSchoolIdsByAnswers: (async () => {
        throw new Error("Eligibility service is disabled");
      }) as GetIneligibleSchoolIdsByAnswersFn,
      getIneligibleSchoolIdsByFormId: (async () => {
        throw new Error("Eligibilty service is disabled");
      }) as GetIneligibleSchoolIdsByFormIdFn,
      shouldRunEligibilityCheck,
      enabled: false,
    };
  }

  return service;
}

export class EligibilityServiceError extends Error {}

/**
 * Check whether eligibilility check should be run again
 */
export const shouldRunEligibilityCheck: ShouldRunEligibilityCheckFn = ({
  questions,
  answers,
}) => {
  const { previous, current } = answers;

  // Check if grade answers has changed
  const previousGradeAnswer = Answer.findGradeAnswer(questions, previous);
  const currentGradeAnswer = Answer.findGradeAnswer(questions, current);
  if (!_.isEqual(previousGradeAnswer, currentGradeAnswer)) {
    return true;
  }

  // check if geo-eligibility is enabled, and address answers has changed
  const geoEligibilityQuestion = Question.findGeoEligibilityQuestion(questions);
  if (geoEligibilityQuestion) {
    const previousAddressAnswer = Answer.findFormAddressAnswer(
      questions,
      previous
    );
    const currentAddressAnswer = Answer.findFormAddressAnswer(
      questions,
      current
    );
    if (!_.isEqual(previousAddressAnswer, currentAddressAnswer)) {
      return true;
    }
  }

  // check if any of the eligibility question answer has changed
  const eligiblityQuestions = getAllEligibilityQuestions(questions);
  for (const eligiblityQuestion of eligiblityQuestions) {
    const previousAnswer = previous[eligiblityQuestion.id];
    const currentAnswer = current[eligiblityQuestion.id];
    if (!_.isEqual(previousAnswer, currentAnswer)) {
      return true;
    }
  }

  return false;
};

function toPayload(
  answers: Readonly<Answer.FormikValues>,
  dateOfBirth: string | null
): Payload {
  const questionIdToAnswer: QuestionIdToAnswer = {};

  for (const questionId of Object.keys(answers)) {
    const answer = answers[questionId];
    if (!answer) {
      continue;
    }

    if (Answer.isSingleSelectAnswer(answer) || Answer.isGradesAnswer(answer)) {
      questionIdToAnswer[questionId] = answer;
    } else if (Answer.isAddressAnswer(answer)) {
      questionIdToAnswer[questionId] = {
        streetAddress: answer.street_address,
        streetAddressLine2: answer.street_address_line_2,
        city: answer.city,
        state: answer.state,
        zipCode: answer.zip_code,
      };
    }
  }

  return {
    applicationType: "Apply",
    studentBirthDate: dateOfBirth ?? undefined,
    questionIdToAnswer: questionIdToAnswer,
  };
}

/* Schemas */
const AddressAnswerSchema = z.object({});
const AnswerSchema = z.union([
  z.string(),
  z.array(z.string()),
  AddressAnswerSchema,
]);

const QuestionIdToAnswerSchema = z.record(z.string(), AnswerSchema);
type QuestionIdToAnswer = z.infer<typeof QuestionIdToAnswerSchema>;
const _PayloadSchema = z.object({
  questionIdToAnswer: QuestionIdToAnswerSchema,
  applicationType: z.literal("Apply"),
  studentBirthDate: z.string().optional(),
});
type Payload = z.infer<typeof _PayloadSchema>;
const SchoolSchema = z.object({
  id: z.string(),
});
const ResponseSchema = z.object({
  ineligibleSchools: z.array(SchoolSchema),
});
